import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Check, Loader2, CreditCard } from "lucide-react";
import { MONTHLY_PRICE_GBP, formatGBP } from "@/lib/pricing";

type Props = {
  showManageButton?: boolean;
  showPrice?: boolean;
  onCancel?: () => void;
  onReactivate?: () => void;
  onResubscribe?: () => void;
  actionLoading?: boolean;
};

type SubStatus = {
  subscribed: boolean;
  subscription_status: string | null;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at_period_end?: boolean;
};

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

const SubscriptionSummaryCard = ({
  showManageButton = true,
  showPrice = false,
  onCancel,
  onReactivate,
  onResubscribe,
  actionLoading = false,
}: Props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SubStatus | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("check-subscription");
        if (error) throw error;
        setStatus(data as SubStatus);
      } catch (e: any) {
        toast.error(`Could not load subscription: ${e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const s = status?.subscription_status;
  const isTrialing = s === "trialing";
  const isActive = s === "active";
  const isPastDue = s === "past_due" || s === "unpaid";
  const isCanceled = !s || s === "canceled" || s === "incomplete_expired";
  const hasSubscription = isTrialing || isActive || isPastDue;
  const willCancel = !!status?.cancel_at_period_end && hasSubscription;
  const endDate = status?.trial_end && isTrialing ? status.trial_end : status?.current_period_end;

  const showActions =
    showManageButton ||
    (hasSubscription && !willCancel && !!onCancel) ||
    (willCancel && !!onReactivate) ||
    (isCanceled && !!onResubscribe);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription
        </CardTitle>
        <CardDescription>Manage your Hound Pro subscription</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-semibold">Hound Pro</h3>
                {isTrialing && <Badge variant="secondary">Free Trial</Badge>}
                {isActive && !willCancel && <Badge className="bg-green-600 hover:bg-green-700">Active</Badge>}
                {willCancel && <Badge variant="outline">Cancelling</Badge>}
                {isPastDue && <Badge variant="destructive">Payment issue</Badge>}
                {isCanceled && <Badge variant="outline">Canceled</Badge>}
              </div>
              {showPrice && (
                <p className="text-sm text-muted-foreground mt-2">
                  {formatGBP(MONTHLY_PRICE_GBP)} / month inc VAT
                </p>
              )}
              {isTrialing && status?.trial_end && !willCancel && (
                <p className="text-sm text-muted-foreground mt-2">
                  Trial ends {formatDate(status.trial_end)}. First charge on that date.
                </p>
              )}
              {willCancel && endDate && (
                <p className="text-sm text-muted-foreground mt-2">
                  Your subscription ends on {formatDate(endDate)}. You'll keep access until then.
                </p>
              )}
              {isPastDue && (
                <p className="text-sm text-destructive mt-2">
                  Your last payment failed. Please reactivate from the subscription page.
                </p>
              )}
              {isCanceled && (
                <p className="text-sm text-muted-foreground mt-2">
                  Your subscription has ended. Reactivate to regain access.
                </p>
              )}
            </div>

            {showActions && (
              <div className="flex flex-col gap-2 min-w-[180px]">
                {showManageButton && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate("/account/subscription")}
                  >
                    Manage subscription
                  </Button>
                )}
                {hasSubscription && !willCancel && onCancel && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={actionLoading}>
                        Cancel subscription
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your subscription will stay active until {formatDate(endDate || null)}, then end automatically.
                          You can reactivate any time before that date.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep subscription</AlertDialogCancel>
                        <AlertDialogAction onClick={onCancel}>
                          Yes, cancel
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {willCancel && onReactivate && (
                  <Button onClick={onReactivate} disabled={actionLoading}>
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reactivate subscription"}
                  </Button>
                )}
                {isCanceled && onResubscribe && (
                  <Button onClick={onResubscribe} disabled={actionLoading}>
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reactivate subscription"}
                  </Button>
                )}
              </div>
            )}
          </div>

          <ul className="mt-6 space-y-2 text-sm">
            {[
              "Unlimited customers & bank details",
              "50 SMS usage per month",
              "Unlimited payment requests (Email)",
              "Automatic payment reminders",
              "Paid / unpaid dashboard",
              "Branded PDF invoices & receipts",
              "Automatic review requests",
              "iPhone & Android apps",
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionSummaryCard;
